import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { prefixKey } from '@/utils/storagePrefix';

export type DataMode = 'demo' | 'live';

export interface ScreenDataMode {
  screenId: string;
  screenName: string;
  mode: DataMode;
  category?: string;
}

interface DataSourceContextType {
  // Global mode (legacy support)
  mode: DataMode;
  setMode: (mode: DataMode) => void;
  isDemo: boolean;
  isLive: boolean;
  isModeLocked: boolean;
  
  // Per-screen mode configuration
  screenModes: Record<string, DataMode>;
  getScreenMode: (screenId: string) => DataMode;
  setScreenMode: (screenId: string, mode: DataMode) => void;
  getAllScreenModes: () => ScreenDataMode[];
  resetAllScreenModes: (mode: DataMode) => void;
}

const DataSourceContext = createContext<DataSourceContextType | undefined>(undefined);

const STORAGE_KEY = prefixKey('timetrack-data-source');
const SCREEN_MODES_KEY = prefixKey('timetrack-screen-modes');
const VERSION_KEY = prefixKey('timetrack-version');
const CURRENT_VERSION = 'main';

// Environment-aware default mode
// In production builds (app-prod), new users default to 'live' mode.
// This covers all deployed environments (prod, preprod, etc.).
// Only local dev builds default to 'demo' mode.
const getDefaultMode = (): DataMode => {
  if (import.meta.env.MODE === 'production') {
    return 'live';
  }
  return 'demo';
};

// Default screen configurations
const DEFAULT_SCREENS: ScreenDataMode[] = [
  // Main Navigation
  { screenId: 'homepage', screenName: 'Homepage', mode: getDefaultMode(), category: 'Main' },
  { screenId: 'timesheet', screenName: 'My Timesheet', mode: getDefaultMode(), category: 'Main' },
  { screenId: 'approvals', screenName: 'Approvals', mode: getDefaultMode(), category: 'Main' },
  { screenId: 'my-delegations', screenName: 'My Delegations', mode: getDefaultMode(), category: 'Main' },
  
  // Reports & Analytics
  { screenId: 'reports-timesheet', screenName: 'Timesheet Reports', mode: getDefaultMode(), category: 'Reports' },
  { screenId: 'reports-utilisation', screenName: 'Utilisation Reports', mode: getDefaultMode(), category: 'Reports' },
  { screenId: 'reports-billable', screenName: 'Billable Hours', mode: getDefaultMode(), category: 'Reports' },
  { screenId: 'reports-project', screenName: 'Project Reports', mode: getDefaultMode(), category: 'Reports' },
  { screenId: 'reports-missing-timesheets', screenName: 'Missing Timesheets', mode: getDefaultMode(), category: 'Reports' },
  { screenId: 'analytics', screenName: 'Analytics', mode: getDefaultMode(), category: 'Reports' },
  
  // Team Management
  { screenId: 'team-members', screenName: 'Team Members', mode: getDefaultMode(), category: 'Team' },
  { screenId: 'team-schedule', screenName: 'Team Schedule', mode: getDefaultMode(), category: 'Team' },
  { screenId: 'team-capacity', screenName: 'Capacity Planning', mode: getDefaultMode(), category: 'Team' },
  
  // Projects
  { screenId: 'projects-active', screenName: 'Active Projects', mode: getDefaultMode(), category: 'Projects' },
  { screenId: 'projects-archive', screenName: 'Archived Projects', mode: getDefaultMode(), category: 'Projects' },
  { screenId: 'projects-budget', screenName: 'Budget Tracking', mode: getDefaultMode(), category: 'Projects' },
  
  // Integrations
  { screenId: 'integrations', screenName: 'Integrations', mode: getDefaultMode(), category: 'Integrations' },
  
  // Data Import/Export
  { screenId: 'import', screenName: 'Import Data', mode: getDefaultMode(), category: 'Data' },
  { screenId: 'export', screenName: 'Export Data', mode: getDefaultMode(), category: 'Data' },
  
  // Settings (last section)
  { screenId: 'settings-general', screenName: 'General', mode: getDefaultMode(), category: 'Settings' },
  { screenId: 'roles-rates', screenName: 'Roles & Rates', mode: getDefaultMode(), category: 'Settings' },
  { screenId: 'roles-permissions', screenName: 'Roles & Permissions', mode: getDefaultMode(), category: 'Settings' },
  { screenId: 'settings-approvals', screenName: 'Approvals', mode: getDefaultMode(), category: 'Settings' },
  { screenId: 'settings-notifications', screenName: 'Notifications', mode: getDefaultMode(), category: 'Settings' },
  { screenId: 'approval-delegation', screenName: 'Approval Delegation', mode: getDefaultMode(), category: 'Settings' },
  { screenId: 'approval-audit', screenName: 'Approval Audit', mode: getDefaultMode(), category: 'Settings' },
];

export const DataSourceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const isModeLocked = localStorage.getItem('timetrack-data-source-locked') === 'true';
  // When locked, the entry point sets the unprefixed 'timetrack-data-source' key.
  // Read it directly so the locked mode matches what the entry point intended,
  // regardless of the prefixed key's value or getDefaultMode().
  const lockedMode = isModeLocked
    ? (localStorage.getItem('timetrack-data-source') as DataMode) || getDefaultMode()
    : null;

  // Global mode (legacy)
  const [mode, setModeState] = useState<DataMode>(() => {
    if (lockedMode) return lockedMode;

    const savedVersion = localStorage.getItem(VERSION_KEY);
    
    if (savedVersion !== CURRENT_VERSION) {
      localStorage.setItem(VERSION_KEY, CURRENT_VERSION);
      const defaultMode = getDefaultMode();
      localStorage.setItem(STORAGE_KEY, defaultMode);
      // Also reset per-screen modes so they pick up the correct environment default
      const defaultScreenModes: Record<string, DataMode> = {};
      DEFAULT_SCREENS.forEach(screen => {
        defaultScreenModes[screen.screenId] = defaultMode;
      });
      localStorage.setItem(SCREEN_MODES_KEY, JSON.stringify(defaultScreenModes));
      return defaultMode;
    }
    
    const saved = localStorage.getItem(STORAGE_KEY);
    return (saved as DataMode) || getDefaultMode();
  });

  // Per-screen modes
  const [screenModes, setScreenModes] = useState<Record<string, DataMode>>(() => {
    if (lockedMode) {
      const modes: Record<string, DataMode> = {};
      DEFAULT_SCREENS.forEach(screen => {
        modes[screen.screenId] = lockedMode;
      });
      return modes;
    }

    const saved = localStorage.getItem(SCREEN_MODES_KEY);
    if (saved) {
      try {
        return JSON.parse(saved) as Record<string, DataMode>;
      } catch {
        // If parsing fails, initialize with defaults
      }
    }
    
    // Initialize with default modes
    const defaultModes: Record<string, DataMode> = {};
    DEFAULT_SCREENS.forEach(screen => {
      defaultModes[screen.screenId] = screen.mode;
    });
    return defaultModes;
  });

  // Persist global mode
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  // Persist screen modes
  useEffect(() => {
    localStorage.setItem(SCREEN_MODES_KEY, JSON.stringify(screenModes));
  }, [screenModes]);

  const setMode = (newMode: DataMode) => {
    if (isModeLocked) return;
    setModeState(newMode);
  };

  const getScreenMode = (screenId: string): DataMode => {
    return screenModes[screenId] || getDefaultMode();
  };

  const setScreenMode = (screenId: string, newMode: DataMode) => {
    if (isModeLocked) return;
    setScreenModes(prev => ({
      ...prev,
      [screenId]: newMode,
    }));
  };

  const getAllScreenModes = (): ScreenDataMode[] => {
    return DEFAULT_SCREENS.map(screen => ({
      ...screen,
      mode: screenModes[screen.screenId] || screen.mode,
    }));
  };

  const resetAllScreenModes = (newMode: DataMode) => {
    if (isModeLocked) return;
    const newModes: Record<string, DataMode> = {};
    DEFAULT_SCREENS.forEach(screen => {
      newModes[screen.screenId] = newMode;
    });
    setScreenModes(newModes);
  };

  const value: DataSourceContextType = {
    mode,
    setMode,
    isDemo: mode === 'demo',
    isLive: mode === 'live',
    isModeLocked,
    screenModes,
    getScreenMode,
    setScreenMode,
    getAllScreenModes,
    resetAllScreenModes,
  };

  return (
    <DataSourceContext.Provider value={value}>
      {children}
    </DataSourceContext.Provider>
  );
};

export const useDataSource = () => {
  const context = useContext(DataSourceContext);
  if (context === undefined) {
    throw new Error('useDataSource must be used within a DataSourceProvider');
  }
  return context;
};
